import { useCallback, useContext, useRef } from 'react';
import { configurationDataAuditFromResource, consentRolesFromData, hasUnreadableStoredRoles, isPtsOnlyConsentDocument, normalizeCustomDataRows, normalizeDocumentTitle, normalizeTemplateCustomFields, projectDocumentToMomFields, readableConsentRolesFromData, } from 'libs.consent.common';
import { FirebaseAuthContext } from 'libs.firebase_react';
import { ResourceType } from 'libs.library_manager';
import { useLocalizeMessage } from 'libs.nucleus';
import { ApiClientService, useApiClient, useJobChecker } from 'libs.react_internal';
import { groupConsentTemplateRows, } from '../consent_view/consent_manager_ui/group_consent_template_rows';
import { carryForwardElements, carryForwardFromResource, coerceInheritedPlacements, } from '../utils/carry_forward_elements';
import { TEMPLATE_DATA_DAMAGED_MESSAGE, validateStoredConsentDocumentForPublish, } from '../utils/consent_publish_validation';
import { consentAssetMimeType } from '../utils/consent_upload_file.helpers';
const RESOURCE_TYPE = ResourceType.CONSENT_TEMPLATE;
const ASSET_RESOURCE_TYPE = ResourceType.CONSENT_ASSET;
const PUBLISHED_TEMPLATE_POLL_INTERVAL_MS = 250;
const PUBLISHED_TEMPLATE_POLL_ATTEMPTS = 12;
/**
 * Library lists versions, the table pages families. A lean walk records unique keys in Library
 * order (newest first) so each UI page can take 50 families. Only those keys are fetched with
 * table `paths`. Expand stays on the page because sibling versions of those keys are included.
 * Full `elements` load with getResource when the editor opens.
 */
const FAMILY_PAGE_SIZE = 50;
const LEAN_LIST_PAGE_SIZE = 200;
const FAMILY_KEY_CHUNK_SIZE = 200;
const FAMILY_VERSION_PAGE_SIZE = 200;
const MAX_LEAN_LIST_PAGES = 100;
const MAX_FAMILY_VERSION_PAGES = 100;
const CONSENT_TEMPLATE_CATALOG_PATHS = ['description', 'ec__language', 'language', 'ec__delayed_signing'];
const unwrapArchiveImpact = (payload) => {
    if (payload && typeof payload === 'object' && 'canArchive' in payload) {
        return payload;
    }
    return payload.data;
};
const ARCHIVE_SYNC_FAILED_MESSAGE = 'Consent template archive did not complete successfully.';
const unwrapSyncJobIds = (payload) => {
    if (!payload || typeof payload !== 'object') {
        return [];
    }
    const record = payload;
    const ids = Array.isArray(record.syncJobIds)
        ? record.syncJobIds
        : Array.isArray(record.data?.syncJobIds)
            ? record.data.syncJobIds
            : [];
    return ids.filter((id) => typeof id === 'string' && id.length > 0);
};
const hasActiveListQuery = (query) => Boolean(query?.search?.trim()) || Boolean(query?.status && query.status !== 'all');
const familyMatchesStatus = (entry, status) => {
    if (!status || status === 'all') {
        return true;
    }
    if (status === 'published') {
        return entry.statuses.includes('PUBLISHED');
    }
    // Same rule as groupConsentTemplateRows: Draft lists families whose latest version is Draft.
    // IN_REVIEW is not Draft; those rows stay on All.
    if (status === 'draft') {
        return entry.latestStatus === 'DRAFT';
    }
    if (status === 'archived') {
        return entry.statuses.includes('ARCHIVED');
    }
    return true;
};
const listQueryStatus = (query) => query?.status ?? 'all';
const listLeanConsentTemplatePage = async (get, entityId, studyId, page) => {
    const { data } = await get(`v1/${RESOURCE_TYPE}`, {
        params: {
            entityId,
            studyId,
            active: true,
            limit: LEAN_LIST_PAGE_SIZE,
            page,
            lean: true,
        },
    });
    const resources = Array.isArray(data.data) ? data.data : [];
    return { resources, total: typeof data.total === 'number' ? data.total : resources.length };
};
const collectFamilyCatalog = async (get, entityId, studyId) => {
    const families = [];
    const byKey = new Map();
    let fetched = 0;
    for (let page = 1; page <= MAX_LEAN_LIST_PAGES; page += 1) {
        const { resources, total } = await listLeanConsentTemplatePage(get, entityId, studyId, page);
        fetched += resources.length;
        for (const resource of resources) {
            if (!resource.key) {
                continue;
            }
            const existing = byKey.get(resource.key);
            if (!existing) {
                const entry = {
                    key: resource.key,
                    names: resource.name ? [resource.name] : [],
                    latestStatus: resource.status,
                    statuses: resource.status ? [resource.status] : [],
                };
                byKey.set(resource.key, entry);
                families.push(entry);
                continue;
            }
            if (resource.name && !existing.names.includes(resource.name)) {
                existing.names.push(resource.name);
            }
            if (resource.status && !existing.statuses.includes(resource.status)) {
                existing.statuses.push(resource.status);
            }
        }
        if (resources.length === 0 || fetched >= total || resources.length < LEAN_LIST_PAGE_SIZE) {
            break;
        }
    }
    return families;
};
const listConsentTemplateFamilies = async (get, entityId, studyId, keys) => {
    if (keys.length === 0) {
        return [];
    }
    const resources = [];
    // `key` filters families; `limit` caps version *rows*. A 50-family page with more than
    // four versions each already exceeds 200 rows, so each key chunk must walk pages until
    // `total` is reached or a short page arrives.
    for (let offset = 0; offset < keys.length; offset += FAMILY_KEY_CHUNK_SIZE) {
        const chunk = keys.slice(offset, offset + FAMILY_KEY_CHUNK_SIZE);
        let fetched = 0;
        let total = Number.POSITIVE_INFINITY;
        for (let page = 1; page <= MAX_FAMILY_VERSION_PAGES; page += 1) {
            const { data } = await get(`v1/${RESOURCE_TYPE}`, {
                params: {
                    entityId,
                    studyId,
                    active: true,
                    limit: FAMILY_VERSION_PAGE_SIZE,
                    page,
                    paths: CONSENT_TEMPLATE_CATALOG_PATHS,
                    key: chunk,
                },
            });
            const batch = Array.isArray(data.data) ? data.data : [];
            resources.push(...batch);
            fetched += batch.length;
            if (typeof data.total === 'number') {
                total = data.total;
            }
            if (batch.length === 0 || fetched >= total || batch.length < FAMILY_VERSION_PAGE_SIZE) {
                break;
            }
        }
    }
    return resources;
};
const libraryStatusToDisplay = (status) => {
    switch (status) {
        case 'PUBLISHED':
            return 'Published';
        case 'IN_REVIEW':
            return 'In Review';
        case 'ARCHIVED':
            return 'Archived';
        default:
            return 'Draft';
    }
};
/**
 * Drops the Configuration Data rows an author added and left blank, on the way out to the backend.
 * Anything that isn't an array is left exactly as it is: the field is absent on an element that
 * never had one, and stored elements are cast rather than parsed on the way in, so a `null` from
 * Cortex reaches here intact and is not this function's to reinterpret.
 */
const withNormalizedCustomData = (elements) => elements.map((element) => Array.isArray(element.config.ec__custom_data)
    ? {
        ...element,
        config: { ...element.config, ec__custom_data: normalizeCustomDataRows(element.config.ec__custom_data) },
    }
    : element);
const resourceData = (resource) => (resource.data ?? {});
const firstStoredString = (...values) => {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) {
            return value;
        }
    }
    return undefined;
};
const getResourceLanguage = (resource) => {
    const data = resourceData(resource);
    const language = data.ec__language ?? data.language;
    return language || undefined;
};
const resourceToFileRow = (resource) => {
    const data = resourceData(resource);
    return {
        id: resource.id,
        templateKey: resource.key,
        title: resource.name,
        version: resource.version,
        language: getResourceLanguage(resource),
        description: data.description ?? '',
        sigFields: data.sigFields ?? '—',
        sites: data.sites ?? '—',
        status: libraryStatusToDisplay(resource.status),
        createdAt: resource.createdAt,
        updatedAt: resource.updatedAt,
    };
};
const resourceToDocument = (resource) => {
    const data = resourceData(resource);
    const meta = (resource.metadata ?? {});
    const storedDataInvalid = hasUnreadableStoredRoles(data);
    // A missing role list is not the same as a stored empty one. Only a payload that actually
    // carried `roles` or `ec__signer_roles` may say the list is healthy, or a catalog row that
    // omitted roles would clear the damage marker and let autosave write the filtered roles back.
    const rolesWereRead = Array.isArray(data.roles) || Array.isArray(data.ec__signer_roles);
    return {
        id: resource.id,
        key: resource.key,
        name: resource.name,
        language: getResourceLanguage(resource),
        delayedSigning: data.ec__delayed_signing === true,
        description: data.description ?? '',
        status: libraryStatusToDisplay(resource.status),
        assetId: meta.consentAssetId ?? '',
        pdfDataUrl: '',
        totalPages: data.totalPages ?? 0,
        elements: data.elements ?? [],
        roles: storedDataInvalid ? readableConsentRolesFromData(data) : consentRolesFromData(data),
        ...(storedDataInvalid ? { storedDataInvalid: true } : rolesWereRead ? { storedDataInvalid: false } : {}),
        customFields: data.customFields ?? [],
        instructionScreen: data.ec__instruction_screen ?? '',
        completionScreen: data.ec__completion_screen ?? '',
        sourceLabel: firstStoredString(data.sourceLabel, data.ec__source_label),
        sourceVersion: firstStoredString(data.sourceVersion, data.ec__source_version),
        sourceVersionDate: firstStoredString(data.sourceVersionDate, data.ec__source_version_date),
        sourceFileName: firstStoredString(data.sourceFileName, data.fileName),
        configurationDataAudit: configurationDataAuditFromResource(data.configurationDataAudit),
        carryForward: carryForwardFromResource(data.carryForward),
        createdAt: resource.createdAt,
        updatedAt: resource.updatedAt,
    };
};
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
export const useConsentLibrary = (entityId, studyId) => {
    const { firebaseToken } = useContext(FirebaseAuthContext);
    const { get, post, put, delete: del } = useApiClient(ApiClientService.LIBRARY);
    const { get: getMom, post: postMom } = useApiClient(ApiClientService.MOM);
    const { post: postCloud } = useApiClient(ApiClientService.CLOUD, {
        withCredentials: false,
    });
    const { checkJobStatus } = useJobChecker();
    const getRef = useRef(get);
    getRef.current = get;
    const postRef = useRef(post);
    postRef.current = post;
    const putRef = useRef(put);
    putRef.current = put;
    const delRef = useRef(del);
    delRef.current = del;
    const postMomRef = useRef(postMom);
    postMomRef.current = postMom;
    const getMomRef = useRef(getMom);
    getMomRef.current = getMom;
    const postCloudRef = useRef(postCloud);
    postCloudRef.current = postCloud;
    const checkJobStatusRef = useRef(checkJobStatus);
    checkJobStatusRef.current = checkJobStatus;
    const firebaseTokenRef = useRef(firebaseToken);
    firebaseTokenRef.current = firebaseToken;
    const translate = useLocalizeMessage();
    const translateRef = useRef(translate);
    translateRef.current = translate;
    const familyCatalogRef = useRef(null);
    const familyHydrateRef = useRef(null);
    const resetFamilyHydrateCache = useCallback((nextEntityId, nextStudyId) => {
        familyHydrateRef.current = { entityId: nextEntityId, studyId: nextStudyId, versionsByKey: new Map() };
    }, []);
    const invalidateFamilyCaches = useCallback(() => {
        familyCatalogRef.current = null;
        if (entityId && studyId) {
            resetFamilyHydrateCache(entityId, studyId);
        }
        else {
            familyHydrateRef.current = null;
        }
    }, [entityId, studyId, resetFamilyHydrateCache]);
    const resolveFamilyCatalog = useCallback(async (refresh) => {
        if (!entityId || !studyId) {
            return [];
        }
        const cached = familyCatalogRef.current;
        const cacheHit = !refresh && cached && cached.entityId === entityId && cached.studyId === studyId ? cached.families : null;
        const families = cacheHit ?? (await collectFamilyCatalog(getRef.current, entityId, studyId));
        if (!cacheHit) {
            familyCatalogRef.current = { entityId, studyId, families };
            resetFamilyHydrateCache(entityId, studyId);
        }
        return families;
    }, [entityId, studyId, resetFamilyHydrateCache]);
    const hydrateFamilyKeys = useCallback(async (keys) => {
        if (!entityId || !studyId || keys.length === 0) {
            return [];
        }
        let hydrateCache = familyHydrateRef.current;
        if (!hydrateCache || hydrateCache.entityId !== entityId || hydrateCache.studyId !== studyId) {
            resetFamilyHydrateCache(entityId, studyId);
            hydrateCache = familyHydrateRef.current;
        }
        const missingKeys = keys.filter((key) => !hydrateCache.versionsByKey.has(key));
        if (missingKeys.length > 0) {
            const fetched = await listConsentTemplateFamilies(getRef.current, entityId, studyId, missingKeys);
            const grouped = new Map();
            for (const resource of fetched) {
                const versions = grouped.get(resource.key) ?? [];
                versions.push(resource);
                grouped.set(resource.key, versions);
            }
            for (const key of missingKeys) {
                hydrateCache.versionsByKey.set(key, grouped.get(key) ?? []);
            }
        }
        return keys.flatMap((key) => hydrateCache.versionsByKey.get(key) ?? []);
    }, [entityId, studyId, resetFamilyHydrateCache]);
    const loadResources = useCallback(async (page = 1, query) => {
        if (!entityId || !studyId) {
            return { fileRows: [], documents: [], total: 0 };
        }
        const refresh = query?.refresh ?? (page === 1 && !hasActiveListQuery(query));
        const catalog = await resolveFamilyCatalog(refresh);
        const status = listQueryStatus(query);
        const statusKeys = catalog.filter((entry) => familyMatchesStatus(entry, status)).map((entry) => entry.key);
        const search = query?.search ?? '';
        // Search needs description/language, which lean omits. Hydrate the status-matched
        // families once, then let grouping be the only filter so hidden siblings and
        // IN_REVIEW cannot inflate templateTotal.
        if (search.trim()) {
            const resources = await hydrateFamilyKeys(statusKeys);
            const families = groupConsentTemplateRows(resources.map(resourceToFileRow), status, search);
            const pageFamilies = families.slice((page - 1) * FAMILY_PAGE_SIZE, page * FAMILY_PAGE_SIZE);
            const pageKeys = new Set(pageFamilies.map((family) => family.parent.templateKey));
            const pageResources = resources.filter((resource) => pageKeys.has(resource.key));
            return {
                fileRows: pageResources.map(resourceToFileRow),
                documents: pageResources.map(resourceToDocument),
                total: families.length,
            };
        }
        const pageKeys = statusKeys.slice((page - 1) * FAMILY_PAGE_SIZE, page * FAMILY_PAGE_SIZE);
        const resources = await hydrateFamilyKeys(pageKeys);
        return {
            fileRows: resources.map(resourceToFileRow),
            documents: resources.map(resourceToDocument),
            total: statusKeys.length,
        };
    }, [entityId, studyId, resolveFamilyCatalog, hydrateFamilyKeys]);
    const loadAssignmentCatalog = useCallback(async (refresh = false) => {
        if (!entityId || !studyId) {
            return { fileRows: [], documents: [] };
        }
        const catalog = await resolveFamilyCatalog(refresh);
        const resources = await hydrateFamilyKeys(catalog.map((entry) => entry.key));
        return {
            fileRows: resources.map(resourceToFileRow),
            documents: resources.map(resourceToDocument),
        };
    }, [entityId, studyId, resolveFamilyCatalog, hydrateFamilyKeys]);
    const getResource = useCallback(async (id) => {
        const { data } = await getRef.current(`v1/${RESOURCE_TYPE}/${id}`);
        const resource = data.data;
        return {
            fileRow: resourceToFileRow(resource),
            document: resourceToDocument(resource),
        };
    }, []);
    const waitForPublishedTemplateVisibility = useCallback(async (resourceId, key) => {
        for (let attempt = 0; attempt < PUBLISHED_TEMPLATE_POLL_ATTEMPTS; attempt += 1) {
            const { data } = await getRef.current(`v1/${RESOURCE_TYPE}`, {
                params: { key, status: 'PUBLISHED', latest: true },
            });
            if (data.data[0]?.id === resourceId) {
                return;
            }
            await sleep(PUBLISHED_TEMPLATE_POLL_INTERVAL_MS);
        }
        throw new Error(`Published consent template ${resourceId} did not become visible as latest published in time.`);
    }, []);
    const resyncTemplateInMom = useCallback(async (key, triggerReason, resourceId) => {
        if (!entityId || !studyId) {
            return [];
        }
        const { data } = await postMomRef.current(`/v1/entities/${entityId}/studies/${studyId}/econsent/templates/${key}/resync`, triggerReason === 'archive' ? { triggerReason, resourceId } : { triggerReason });
        return unwrapSyncJobIds(data);
    }, [entityId, studyId]);
    const waitForMomSyncJobs = useCallback(async (syncJobIds) => {
        for (const jobId of syncJobIds) {
            const workflow = await checkJobStatusRef.current(jobId);
            if (workflow.status !== 'completed') {
                throw new Error(ARCHIVE_SYNC_FAILED_MESSAGE);
            }
        }
    }, []);
    const getArchiveImpact = useCallback(async (templateKey, resourceId) => {
        if (!entityId || !studyId) {
            throw new Error('Cannot load archive impact without a MOM study scope.');
        }
        const { data } = await getMomRef.current(`/v1/entities/${entityId}/studies/${studyId}/econsent/templates/${templateKey}/archive-impact`, { params: { resourceId } });
        return unwrapArchiveImpact(data);
    }, [entityId, studyId]);
    const uploadConsentAsset = useCallback(async (file, name) => {
        const assetFormData = new FormData();
        assetFormData.append('file', file, file.name);
        assetFormData.append('name', name);
        assetFormData.append('type', ASSET_RESOURCE_TYPE);
        assetFormData.append('status', 'PUBLISHED');
        if (entityId) {
            assetFormData.append('entityId', entityId.toString());
        }
        if (studyId) {
            assetFormData.append('studyId', studyId);
        }
        assetFormData.append('data', JSON.stringify({ fileName: file.name, mimeType: consentAssetMimeType(file) }));
        const { data: assetResponse } = await postRef.current(`v1/${ASSET_RESOURCE_TYPE}`, assetFormData, { headers: { 'Content-Type': 'multipart/form-data' } });
        return assetResponse.data;
    }, [entityId, studyId]);
    const createResource = useCallback(async (file, name, initialData) => {
        // Step 1: upload the PDF as a CONSENT_ASSET
        const asset = await uploadConsentAsset(file, name);
        // Step 2: create the CONSENT_TEMPLATE referencing the asset
        const roles = initialData.roles ?? [];
        const { data: templateResponse } = await postRef.current(`v1/${RESOURCE_TYPE}`, {
            name,
            type: RESOURCE_TYPE,
            status: 'DRAFT',
            ...(entityId ? { entityId: entityId.toString() } : {}),
            ...(studyId ? { studyId } : {}),
            data: {
                elements: [],
                totalPages: 0,
                roles,
                customFields: [],
                ...projectDocumentToMomFields([], roles),
                ...(initialData.language ? { ec__language: initialData.language } : {}),
                sites: initialData.sites ?? '—',
                description: initialData.description ?? '',
                sigFields: '—',
            },
            metadata: {
                consentAssetId: asset.id,
            },
        });
        const resource = templateResponse.data;
        const mapped = resourceToDocument(resource);
        invalidateFamilyCaches();
        return {
            fileRow: {
                ...resourceToFileRow(resource),
                language: initialData.language ?? mapped.language,
                description: initialData.description ?? mapped.description ?? '',
            },
            document: {
                ...mapped,
                roles: roles.length > 0 ? roles : mapped.roles,
                description: initialData.description ?? mapped.description,
                language: initialData.language ?? mapped.language,
            },
        };
    }, [entityId, studyId, uploadConsentAsset, invalidateFamilyCaches]);
    const updateResourceData = useCallback(async (id, dataPayload) => {
        // Every editor save funnels through here, so it is the one place that can guarantee no element
        // reaches the backend carrying Configuration Data rows the author left blank (SSP-4218). Doing it
        // here rather than in the panels also keeps the MOM projection below working from clean rows.
        const { name, ...dataFields } = dataPayload;
        const payload = {
            ...dataFields,
            ...(dataFields.elements !== undefined ? { elements: withNormalizedCustomData(dataFields.elements) } : {}),
            ...(dataFields.customFields !== undefined
                ? { customFields: normalizeTemplateCustomFields(dataFields.customFields) }
                : {}),
        };
        const momFields = payload.elements !== undefined
            ? projectDocumentToMomFields(payload.elements, payload.roles ?? [], payload.customFields ?? [])
            : {};
        const normalizedName = typeof name === 'string' ? normalizeDocumentTitle(name) : '';
        await putRef.current(`v1/${RESOURCE_TYPE}/${id}`, {
            ...(normalizedName ? { name: normalizedName } : {}),
            data: { ...payload, ...momFields },
        });
    }, []);
    const archiveResource = useCallback(async (key, resourceId) => {
        if (!key) {
            throw new Error('Cannot archive a consent template without a template key.');
        }
        if (!resourceId) {
            throw new Error('Cannot archive a consent template without a resource id.');
        }
        if (!entityId || !studyId) {
            throw new Error('Cannot archive a consent template without a MOM study scope.');
        }
        const syncJobIds = await resyncTemplateInMom(key, 'archive', resourceId);
        await waitForMomSyncJobs(syncJobIds);
        invalidateFamilyCaches();
    }, [entityId, studyId, resyncTemplateInMom, waitForMomSyncJobs, invalidateFamilyCaches]);
    // Wires the Library clone endpoint (`mode: 'version'`) that already exists server-side but was
    // previously unused by this UI — the only way to start a new version was informal re-authoring.
    const cloneResource = useCallback(async (id, mode = 'version') => {
        const { data } = await postRef.current(`v1/${RESOURCE_TYPE}/${id}/clone`, { mode });
        const resource = data.data;
        invalidateFamilyCaches();
        return {
            fileRow: resourceToFileRow(resource),
            document: resourceToDocument(resource),
            metadata: (resource.metadata ?? {}),
        };
    }, [invalidateFamilyCaches]);
    // Soft delete is a Draft-only concept, distinct from Archive — a Draft was never
    // published, so it has no MOM assignments or in-flight documents to resync/void, and per the
    // spec ("we do not ARCHIVE draft templates") this must not touch `status` at all.
    // Library's `active` flag (already used to filter every list query — see loadResources above)
    // is the actual soft-delete mechanism: the existing `DELETE /v1/:resourceType/:id` route just
    // flips `active: false`, nothing else.
    const deleteResource = useCallback(async (id) => {
        await delRef.current(`v1/${RESOURCE_TYPE}/${id}`);
        invalidateFamilyCaches();
    }, [invalidateFamilyCaches]);
    // Clones a new version, then optionally retargets it at a freshly-uploaded PDF. If the retarget
    // PUT fails after the clone succeeds, the clone is rolled back via delete so the single-open-draft
    // guard doesn't permanently block retries on this template lineage.
    const cloneResourceWithFile = useCallback(async (id, options) => {
        const { file, keepConfiguration, name, sourceVersion, sourceName, auditAuthor } = options;
        let uploadedAsset;
        if (file) {
            uploadedAsset = await uploadConsentAsset(file, file.name);
        }
        let clonedFileRow;
        let clonedDocument;
        let clonedMetadata;
        try {
            ({
                fileRow: clonedFileRow,
                document: clonedDocument,
                metadata: clonedMetadata,
            } = await cloneResource(id, 'version'));
        }
        catch (cloneError) {
            if (uploadedAsset) {
                try {
                    await delRef.current(`v1/${ASSET_RESOURCE_TYPE}/${uploadedAsset.id}`);
                }
                catch {
                    // ignore — nothing more useful to do than surface the original error below
                }
            }
            throw cloneError;
        }
        // Keeping the configuration would write the readable subset of the damaged roles into the new
        // version, which then publishes a role list nobody authored. Start fresh drops the roles instead.
        if (keepConfiguration && clonedDocument.storedDataInvalid) {
            // One open draft blocks the next New Version. Swallowing a failed delete would leave that
            // draft in place and the retry would fail for a different reason than the damaged data.
            let draftRemoved = false;
            try {
                await delRef.current(`v1/${RESOURCE_TYPE}/${clonedDocument.id}`);
                draftRemoved = true;
                if (uploadedAsset) {
                    await delRef.current(`v1/${ASSET_RESOURCE_TYPE}/${uploadedAsset.id}`);
                }
            }
            catch (cleanupError) {
                const detail = draftRemoved
                    ? `Draft ${clonedDocument.id} was removed, but the uploaded file was not.`
                    : `Draft ${clonedDocument.id} is still open, so a new version stays blocked until that draft is removed.`;
                throw Object.assign(new Error(`${TEMPLATE_DATA_DAMAGED_MESSAGE} ${detail}`), {
                    code: 'TEMPLATE_DATA_INVALID',
                    cause: cleanupError,
                });
            }
            throw Object.assign(new Error(TEMPLATE_DATA_DAMAGED_MESSAGE), { code: 'TEMPLATE_DATA_INVALID' });
        }
        const assetId = uploadedAsset?.id ?? clonedDocument.assetId;
        const carriedAt = new Date().toISOString();
        const trail = clonedDocument.configurationDataAudit ?? [];
        const nextTrail = keepConfiguration && auditAuthor ? [...trail, { at: carriedAt, author: auditAuthor }] : trail;
        let { elements } = clonedDocument;
        let { roles } = clonedDocument;
        let { totalPages } = clonedDocument;
        let carryForward;
        if (!keepConfiguration) {
            elements = [];
            roles = [];
            if (file) {
                try {
                    const { readPdfPageMetrics } = await import('../utils/pdf_page_metrics');
                    totalPages = (await readPdfPageMetrics(file)).pageCount;
                }
                catch {
                    totalPages = 0;
                }
            }
            else {
                totalPages = clonedDocument.totalPages;
            }
        }
        else {
            carryForward = {
                fromResourceId: id,
                fromVersion: sourceVersion,
                fromName: sourceName,
                carriedAt,
                placementsConfirmed: !file,
            };
            if (file) {
                try {
                    let parentUrl = '';
                    if (clonedDocument.assetId) {
                        const { data: parentAsset } = await getRef.current(`v1/${ASSET_RESOURCE_TYPE}/${clonedDocument.assetId}`, { params: { withUrl: true } });
                        parentUrl = parentAsset.data.blobUrl ?? '';
                    }
                    const { readPdfPageMetrics } = await import('../utils/pdf_page_metrics');
                    const parentMetrics = parentUrl ? await readPdfPageMetrics(parentUrl) : { pageCount: 0, sizes: new Map() };
                    const newMetrics = await readPdfPageMetrics(file);
                    elements = carryForwardElements(clonedDocument.elements, newMetrics.pageCount, parentMetrics.sizes, newMetrics.sizes);
                    totalPages = newMetrics.pageCount;
                }
                catch {
                    elements = [];
                    totalPages = 0;
                    carryForward = { ...carryForward, placementsConfirmed: true, failed: true };
                }
            }
            else {
                elements = coerceInheritedPlacements(clonedDocument.elements);
            }
        }
        try {
            const customFields = clonedDocument.customFields ?? [];
            const { data } = await putRef.current(`v1/${RESOURCE_TYPE}/${clonedDocument.id}`, {
                ...(name?.trim() ? { name: name.trim() } : {}),
                metadata: { ...clonedMetadata, consentAssetId: assetId },
                data: {
                    elements,
                    totalPages,
                    roles,
                    customFields,
                    configurationDataAudit: nextTrail,
                    ...(carryForward ? { carryForward } : {}),
                    ...projectDocumentToMomFields(elements, roles, customFields),
                    ...(clonedDocument.language ? { ec__language: clonedDocument.language } : {}),
                    // The PUT replaces `data`, and an unset flag lets MOM unlock new documents without a site release.
                    // Start fresh drops it: the empty draft reads as print-to-sign only while roles are added
                    // before elements, and the panel clears the flag in that state.
                    ec__delayed_signing: keepConfiguration &&
                        !isPtsOnlyConsentDocument({ roles, elements }) &&
                        clonedDocument.delayedSigning === true,
                    description: clonedFileRow.description,
                    sites: clonedFileRow.sites,
                    sigFields: clonedFileRow.sigFields,
                    ec__instruction_screen: clonedDocument.instructionScreen ?? '',
                    ec__completion_screen: clonedDocument.completionScreen ?? '',
                },
            });
            const resource = data.data;
            return {
                fileRow: resourceToFileRow(resource),
                document: resourceToDocument(resource),
            };
        }
        catch (retargetError) {
            try {
                await delRef.current(`v1/${RESOURCE_TYPE}/${clonedDocument.id}`);
            }
            catch {
                throw Object.assign(new Error('Failed to attach the uploaded file to the new version, and failed to clean up the partial clone. Contact support.'), { code: 'ROLLBACK_FAILED' });
            }
            throw retargetError;
        }
    }, [uploadConsentAsset, cloneResource]);
    const readTemplateMetadata = useCallback(async (id) => {
        const { data } = await getRef.current(`v1/${RESOURCE_TYPE}/${id}`);
        return { ...(data.data.metadata ?? {}) };
    }, []);
    const replaceDocumentAsset = useCallback(async (id, file) => {
        const asset = await uploadConsentAsset(file, file.name);
        // Library PUT replaces `metadata` wholesale (clone path spreads for the same reason).
        // The previous CONSENT_ASSET is left in place: a cloned draft may still share its parent's.
        const metadata = await readTemplateMetadata(id);
        await putRef.current(`v1/${RESOURCE_TYPE}/${id}`, {
            metadata: { ...metadata, consentAssetId: asset.id },
        });
        return asset.id;
    }, [readTemplateMetadata, uploadConsentAsset]);
    const clearDocumentAsset = useCallback(async (id) => {
        const metadata = await readTemplateMetadata(id);
        await putRef.current(`v1/${RESOURCE_TYPE}/${id}`, {
            metadata: { ...metadata, consentAssetId: '' },
        });
    }, [readTemplateMetadata]);
    const getResourcePdfUrl = useCallback(async (assetId) => {
        const { data } = await getRef.current(`v1/${ASSET_RESOURCE_TYPE}/${assetId}`, {
            params: { withUrl: true },
        });
        return data.data.blobUrl ?? '';
    }, []);
    const bundleConsentTemplate = useCallback(async (resourceId) => {
        // Same as eCOA instrument_bundler: use the Firebase ID token from auth context.
        // After Auth Emulator restarts, reload / re-login if the cached token was revoked.
        const token = firebaseTokenRef.current;
        if (!token) {
            throw new Error('Firebase auth session is unavailable. Reload the page and try again.');
        }
        // The editor modal reports a bundler 400 itself (the rule it failed on), so the shared
        // interceptor must not add its own "Bad request" toast carrying the internal envelope message.
        await postCloudRef.current('/consent_bundler/v1/bundle', { resourceId }, { headers: { Authorization: `Bearer ${token}` }, no400Handling: true });
    }, []);
    const publishTemplate = useCallback(async (document) => {
        const validationError = validateStoredConsentDocumentForPublish(document);
        if (validationError) {
            throw new Error(translateRef.current(validationError.message, validationError.params));
        }
        // HACK: The library API does not permit a direct DRAFT → PUBLISHED transition.
        // Permitted transitions from DRAFT are IN_REVIEW and ARCHIVED; from IN_REVIEW
        // the transition to PUBLISHED is valid. We go DRAFT → IN_REVIEW → PUBLISHED
        // as a workaround until the business requirements for IN_REVIEW are finalised.
        await putRef.current(`v1/${RESOURCE_TYPE}/${document.id}`, { status: 'IN_REVIEW' });
        await putRef.current(`v1/${RESOURCE_TYPE}/${document.id}`, { status: 'PUBLISHED' });
        if (entityId && studyId) {
            // Temporary hack: poll the same "latest published by key" lookup MOM uses until the
            // newly published resource is visible there, then trigger MOM resync.
            await waitForPublishedTemplateVisibility(document.id, document.key);
            await resyncTemplateInMom(document.key, 'publish');
        }
        invalidateFamilyCaches();
    }, [entityId, studyId, waitForPublishedTemplateVisibility, resyncTemplateInMom, invalidateFamilyCaches]);
    return {
        loadResources,
        loadAssignmentCatalog,
        getResource,
        createResource,
        updateResourceData,
        archiveResource,
        getArchiveImpact,
        cloneResource,
        deleteResource,
        cloneResourceWithFile,
        replaceDocumentAsset,
        clearDocumentAsset,
        getResourcePdfUrl,
        bundleConsentTemplate,
        publishTemplate,
    };
};
